import { GlobalQueryParams, IEntity, IEquatable, IPagedResult, IQuerable, Pagination, QueryParams, Where, WhereField } from '../types';
import { isObject, isUnresolvedRelation } from '../utils';

export class JsonService<T extends IEntity = IEntity> implements IQuerable<T> {
  items: T[];

  constructor(items: T[]) {
    this.items = items;
  }

  async getItems<B extends T = T>(): Promise<B[]> {
    return this.items as B[];
  }

  protected whereId_(id: IEquatable): WhereField {
    return (
      typeof id === 'string' && /^[0-9]+$/.test(id) ? ({
        in: [id, parseInt(id)],
      }) : ({
        equals: id,
      })
    );
  }

  async findGlobal<B extends T = T>(params: GlobalQueryParams = {}): Promise<B | undefined> {
    const items = await this.getItems<B>();
    // console.log('JsonService.findGlobal', params);
    // console.log('JsonService.findOne', items);
    if (items.length > 0) {
      return await this.decorateOne_<B>(items[0], params);
    } else {
      console.log('JsonService.findGlobal.notfound', params);
      return undefined;
    }
  }

  async findOne<B extends T = T>(id: IEquatable, params: QueryParams = {}): Promise<B | undefined> {
    let items = await this.getItems<B>();
    params.where = params.where || {};
    params.where.id = params.where.id || this.whereId_(id);
    // console.log('JsonService.findOne', id, params);
    items = this.whereItems_<B>(items, params.where);
    // console.log('JsonService.findOne', items);
    if (items.length > 0) {
      return await this.decorateOne_<B>(items[0]!, params);
    } else {
      console.log('JsonService.findOne.notfound', id, params);
      return undefined;
    }
  }

  async findMany<B extends T = T>(params: QueryParams = {}): Promise<B[]> {
    params.pagination = false;
    // console.log('JsonService.findMany', params);
    let items = await this.getItems<B>();
    if (params.where) {
      items = this.whereItems_<B>(items, params.where, params.limit);
    }
    if (params.sort) {
      items = this.sortCollection_<B>(items, params.sort);
    }
    if (params.limit && !params.where) {
      items = this.limitCollection_<B>(items, params.limit);
    }
    // console.log(params.pagination, items.length, items[0]);
    // console.log('JsonService.findMany', items);
    return await this.decorateMany_(items, params);
  }

  async findPaged<B extends T = T>(params: QueryParams = {}): Promise<IPagedResult<B>> {
    params.pagination = true;
    // console.log('JsonService.findPaged', params);
    let items = await this.getItems<B>();
    if (params.where) {
      items = this.whereItems_<B>(items, params.where);
    }
    if (params.sort) {
      items = this.sortCollection_<B>(items, params.sort);
    }
    const pagination = this.paginatedItems_<B>(items, params.page, params.limit);
    pagination.docs = await this.decorateMany_(pagination.docs, params);
    return pagination;
  }

  async create<B extends T = T>(payload: Omit<B, 'id'>): Promise<B> {
    const items = await this.getItems<B>();
    const item = { ...payload, id: this.newUUID_() } as B;
    this.items = [...items, item];
    return item;
  }

  async updateOne<B extends T = T>(id: IEquatable, payload: Partial<B>, params: QueryParams = {}): Promise<B> {
    let items = await this.getItems<B>();
    params.where = params.where || {};
    params.where.id = params.where.id || this.whereId_(id);
    // console.log('JsonService.updateOne', id, params);
    const whereItems = this.whereItems_<B>(items, params.where);
    // console.log('JsonService.updateOne', whereItems);
    if (whereItems.length > 0) {
      const index = items.indexOf(whereItems[0]!);
      const item = { ...items[index], payload } as unknown as B;
      items = [...items];
      items[index] = item;
      this.items = items;
      return await this.decorateOne_<B>(item, params);
    }
    throw ('not found');
  }

  async updateMany<B extends T = T>(payload: Partial<B>, params: QueryParams = {}): Promise<B[]> {
    params.pagination = false;
    // console.log('JsonService.updateMany', params);
    let items = await this.getItems<B>();
    let filteredItems = items;
    if (params.where) {
      filteredItems = this.whereItems_<B>(filteredItems, params.where, params.limit);
    }
    if (params.sort) {
      filteredItems = this.sortCollection_<B>(filteredItems, params.sort);
    }
    if (params.limit && !params.where) {
      filteredItems = this.limitCollection_<B>(filteredItems, params.limit);
    }
    items = [...items];
    filteredItems = filteredItems.map(x => {
      const index = items.indexOf(x);
      const item = { ...items[index], payload } as unknown as B;
      items[index] = item;
      return item;
    });
    this.items = items;
    return await this.decorateMany_(filteredItems, params);
  }

  async deleteOne<B extends T = T>(id: IEquatable, params: QueryParams = {}): Promise<B | undefined> {
    let items = await this.getItems<B>();
    params.where = params.where || {};
    params.where.id = params.where.id || this.whereId_(id);
    // console.log('JsonService.deleteOne', id, params);
    const whereItems = this.whereItems_<B>(items, params.where);
    // console.log('JsonService.deleteOne', whereItems);
    if (whereItems.length > 0) {
      const index = items.indexOf(whereItems[0]!);
      if (index !== -1) {
        const item = items[index]!;
        items = [...items];
        items.splice(index, 1);
        this.items = items;
        return await this.decorateOne_<B>(item, params);
      }
      return undefined;
    }
    return undefined;
  }

  async deleteMany<B extends T = T>(params: QueryParams = {}): Promise<B[]> {
    params.pagination = false;
    // console.log('JsonService.deleteMany', params);
    let items = await this.getItems<B>();
    let filteredItems = items;
    if (params.where) {
      filteredItems = this.whereItems_<B>(filteredItems, params.where, params.limit);
    }
    if (params.sort) {
      filteredItems = this.sortCollection_<B>(filteredItems, params.sort);
    }
    if (params.limit && !params.where) {
      filteredItems = this.limitCollection_<B>(filteredItems, params.limit);
    }
    items = [...items];
    this.items = items.filter(x => filteredItems.indexOf(x) === -1);
    return await this.decorateMany_(filteredItems, params);
  }

  // !!! old

  async update<B extends T = T>(payload: Partial<B>): Promise<B> {
    let items = await this.getItems<B>();
    const index = items.reduce((p, c, i) => {
      return c.id === payload.id ? i : p;
    }, -1);
    if (index !== -1) {
      const item = { ...items[index], payload } as unknown as B;
      items = [...items];
      items[index] = item;
      this.items = items;
      return item;
    }
    throw ('not found');
  }

  async delete<B extends T = T>(id: IEquatable): Promise<B | undefined> {
    let items = await this.getItems<B>();
    const index = items.reduce((p, c, i) => {
      return c.id === id ? i : p;
    }, -1);
    if (index !== -1) {
      const item = items[index];
      items = [...items];
      items.splice(index, 1);
      this.items = items;
      return item;
    }
    return undefined;
  }

  protected async decorateOne_<B extends T = T>(item: B, params: QueryParams = {}): Promise<B> {
    return item;
  }

  protected async decorateMany_<B extends T = T>(items: B[], params: QueryParams = {}): Promise<B[]> {
    return items;
  }

  protected newUUID_() {
    return new Date().getTime();
  }

  // where

  protected equalsClause_(value: unknown, equals?: unknown): boolean {
    // console.log('equalsClause_', value, equals);
    if (typeof equals !== 'undefined') {
      return value === equals;
    }
    return true;
  }

  protected notEqualsClause_(value: unknown, not_equals?: unknown): boolean {
    // console.log('notEqualsClause_', value, not_equals);
    if (typeof not_equals !== 'undefined') {
      return value != not_equals;
    }
    return true;
  }

  protected existsClause_(value: unknown, exists?: boolean): boolean {
    // console.log('existsClause_', value, exists);
    if (typeof exists === 'boolean') {
      return (value != null) === exists;
    }
    return true;
  }

  protected greaterThanClause_(value: unknown, greater_than?: unknown): boolean {
    // console.log('greaterThanClause_', value, greater_than);
    if (typeof greater_than === 'number') {
      return typeof value === 'number' && value > greater_than;
    }
    return true;
  }

  protected greaterThanEqualClause_(value: unknown, greater_than_equal?: unknown): boolean {
    // console.log('greaterThanEqualClause_', value, greater_than_equal);
    if (typeof greater_than_equal === 'number') {
      return typeof value === 'number' && value >= greater_than_equal;
    }
    return true;
  }

  protected lessThanClause_(value: unknown, less_than?: unknown): boolean {
    // console.log('lessThanClause_', value, less_than);
    if (typeof less_than === 'number') {
      return typeof value === 'number' && value < less_than;
    }
    return true;
  }

  protected lessThanEqualClause_(value: unknown, less_than_equal?: unknown): boolean {
    // console.log('lessThanEqualClause_', value, less_than_equal);
    if (typeof less_than_equal === 'number') {
      return typeof value === 'number' && value <= less_than_equal;
    }
    return true;
  }

  protected likeClause_(value: unknown, query?: string): boolean {
    // console.log('likeClause_', value, query);
    if (typeof query === 'string') {
      return String(value).toLowerCase().indexOf(query.toLowerCase()) !== -1;
    }
    return true;
  }

  protected containsClause_(value: unknown, contains?: IEquatable): boolean {
    // console.log('containsClause_', value, contains);
    if (typeof contains !== 'undefined') {
      contains = String(contains).toLowerCase();
      if (Array.isArray(value)) {
        return value.reduce((p, c) => (
          p || String(c).toLowerCase() === contains
        ), false);
      } else if (typeof value === 'string') {
        return value.toLowerCase().includes(contains);
      } else {
        return false;
      }
    }
    return true;
  }

  protected inClause_(value: unknown, values?: unknown[]): boolean {
    // console.log('inClause_', value, values);
    if (Array.isArray(values) && values.length > 0) {
      return values.indexOf(value) !== -1;
    }
    return true;
  }

  protected notInClause_(value: unknown, values?: unknown[]): boolean {
    // console.log('notInClause_', value, values);
    if (Array.isArray(values) && values.length > 0) {
      return values.indexOf(value) === -1;
    }
    return true;
  }

  protected allClause_(value: unknown, values?: unknown[]): boolean {
    // console.log('allClause_', value, values);
    if (Array.isArray(values) && values.length > 0) {
      return Array.isArray(value) ?
        values.reduce<boolean>((p: boolean, c: unknown) => (p && value.includes(c)), true) :
        false;
    }
    return true;
  }

  protected nearClause_(value: unknown, near?: unknown[]): boolean {
    if (
      Array.isArray(near) &&
      near.reduce((p, c) => p && typeof c === 'number', true)
    ) {
      if (!Array.isArray(value)) {
        return false;
      }
      const itemLongitude = value[0];
      if (typeof itemLongitude !== 'number') {
        return false;
      }
      const itemLatitude = value[1];
      if (typeof itemLatitude !== 'number') {
        return false;
      }
      const [longitude, latitude, maxDistance, minDistance] = near as number[];
      const distance = longitude && latitude ?
        calculateDistanceInMeters_(itemLongitude, itemLatitude, longitude, latitude) :
        0;
      if (typeof maxDistance === 'number' && typeof minDistance === 'number') {
        return distance <= maxDistance && distance >= minDistance;
      } else if (typeof maxDistance === 'number') {
        return distance <= maxDistance;
      } else if (typeof minDistance === 'number') {
        return distance >= minDistance;
      }
    }
    return true;
  }

  protected whereItem_<B extends T = T>(item: B, where?: Where): boolean {
    let has = true;
    if (typeof where === 'object') {
      Object.entries(where).forEach(([k, v]) => {
        switch (k) {
          case 'and':
            if (Array.isArray(v)) {
              has = v.reduce((p, c) => {
                return p && this.whereItem_(item, c);
              }, has);
            }
            break;
          case 'or':
            if (Array.isArray(v)) {
              has = has && v.reduce((p, c) => {
                return p || this.whereItem_(item, c);
              }, false);
            }
            break;
          default:
            if (Array.isArray(v)) {
              // !!! todo handle Where[];
            } else if (typeof v === 'object') {
              const value = getValue(item, k); // evaluate
              // console.log('getValue', k, value);
              has = has && this.equalsClause_(value, v.equals);
              has = has && this.notEqualsClause_(value, v.not_equals);
              has = has && this.existsClause_(value, v.exists as (boolean | undefined));
              has = has && this.greaterThanClause_(value, v.greater_than);
              has = has && this.greaterThanEqualClause_(value, v.greater_than_equal);
              has = has && this.lessThanClause_(value, v.less_than);
              has = has && this.lessThanEqualClause_(value, v.less_than_equal);
              has = has && this.likeClause_(value, v.like as (string | undefined));
              has = has && this.containsClause_(value, v.contains as (IEquatable | undefined));
              has = has && this.inClause_(value, v.in as (unknown[] | undefined));
              has = has && this.notInClause_(value, v.not_in as (unknown[] | undefined));
              has = has && this.allClause_(value, v.all as (unknown[] | undefined));
              has = has && this.nearClause_(value, v.near as (unknown[] | undefined));
            }
        }
      });
    }
    return has;
  }

  /**
  equals	The value must be exactly equal.
  not_equals	The query will return all documents where the value is not equal.
  exists	Only return documents where the value either exists (true) or does not exist (false).
  greater_than	For numeric or date-based fields.
  greater_than_equal	For numeric or date-based fields.
  less_than	For numeric or date-based fields.
  less_than_equal	For numeric or date-based fields.
  like	Case-insensitive string must be present. If string of words, all words must be present, in any order.
  contains	Must contain the value entered, case-insensitive.
  in	The value must be found within the provided comma-delimited list of values.
  not_in	The value must NOT be within the provided comma-delimited list of values.
  all	The value must contain all values provided in the comma-delimited list.
  near	For distance related to a point field comma separated as <longitude>, <latitude>, <maxDistance in meters (nullable)>, <minDistance in meters (nullable)>.
  */

  protected whereItems_<B extends T = T>(items: B[], where?: Where, limit: number = -1): B[] {
    if (typeof where === 'object') {
      const whereItems = [];
      for (const item of items) {
        if (limit > -1 && whereItems.length >= limit) {
          return whereItems;
        }
        if (this.whereItem_(item, where)) {
          whereItems.push(item);
        }
      }
      return whereItems;
    }
    return items;
  }

  // ^ where

  // sort

  protected sortCollection_<B extends T = T>(items: B[], sort?: string): B[] {
    if (sort) {
      const reverse = sort.indexOf('-') === 0 ? -1 : 1;
      const key: string = sort.replace(/^-/, '');
      items = [...items];
      items.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * reverse;
        }
        const aStringValue = String(aValue);
        const bStringValue = String(bValue);
        return aStringValue.localeCompare(bStringValue) * reverse;
        /*
        const aValue = String(a[key]);
        const bValue = String(b[key]);
        if (aValue.localeCompare(bValue) < 0) {
          return -1 * reverse;
        } else if (aValue.localeCompare(bValue) > 0) {
          return 1 * reverse;
        }
        return 0;
        */
      });
    }
    return items;
  }

  // ^ sort

  // limit

  protected limitCollection_<B extends T = T>(items: B[], limit?: number): B[] {
    if (limit) {
      items = items.slice(0, Math.min(limit, items.length));
    }
    return items;
  }

  // ^ limit

  // pagination

  protected paginatedItems_<B extends T = T>(items: B[], page: number = 1, limit: number = 10): ({ docs: B[] } & Pagination) {
    const totalDocs = items.length;
    const totalPages = Math.ceil(items.length / limit);
    const hasNextPage = page < totalPages;
    const hasPrevPage = page > 1;
    const index = page - 1;
    const from = index * limit;
    const to = Math.min(page * limit, totalDocs);
    const docs = items.slice(from, to);
    const pagination = {
      totalDocs,
      totalPages,
      page,
      limit,
      hasNextPage,
      hasPrevPage,
      nextPage: hasNextPage ? page + 1 : null,
      prevPage: hasPrevPage ? page - 1 : null,
      pagingCounter: from + 1,
    };
    return {
      docs,
      ...pagination,
    };
  }

  // ^ sort
}

function getValue(item: {}, key: string): unknown {
  const keys = key.split('.');
  const value = keys.reduce((p: unknown, c: string) => {
    if (isObject(p)) {
      const v = p[c];
      if (isUnresolvedRelation(v)) {
        return v.value;
      }
      return v;
    } else {
      return undefined;
    }
  }, item);
  return value;
}

function calculateDistanceInMeters_(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad_(lat2 - lat1);
  const dLon = deg2rad_(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad_(lat1)) * Math.cos(deg2rad_(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c * 1000; // Distance in meters
  return distance;
}

function deg2rad_(deg: number): number {
  return deg * (Math.PI / 180);
}
